import React, { useRef, useState } from "react";
import { Box, Button, LinearProgress, MenuItem, Stack, TextField } from "@mui/material";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { AffiliateAccount } from "../../util/api/affiliateApi";
import { productReferralLink, referralProducts } from "./productLinks";

interface Props {
  account: AffiliateAccount;
  busy: boolean;
  onGenerate: () => void;
  onCopy: (value: string, kind?: "link" | "code") => void;
  onLogout: () => void;
}

const panel = { border: "1px solid #d9dfe6", borderRadius: "6px", p: { xs: 3, sm: 4 }, minWidth: 0 };
const eyebrow = { fontSize: "0.625rem", letterSpacing: "0.05em", textTransform: "uppercase", fontWeight: 700 };
const heading = { m: 0, fontSize: { xs: "1.25rem", sm: "1.5rem" }, fontWeight: 600, letterSpacing: "-0.02em", lineHeight: 1.25 };
const linkField = {
  "& .MuiInputBase-root": { color: "#202b3b", bgcolor: "#f7f8fa", borderRadius: "4px", fontSize: "0.8125rem", lineHeight: 1.5 },
  "& .MuiInputLabel-root": { color: "#526075", fontSize: "0.75rem" },
  "& .MuiInputLabel-root.Mui-focused": { color: "#345a96" },
  "&& .MuiOutlinedInput-notchedOutline": { borderColor: "#c4cedb" },
  "&& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#4268a8" },
  "&& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#345a96" },
  "& .MuiInputBase-input": { py: 1.75, px: 1.75 },
};
const destinationMenu = {
  anchorOrigin: { vertical: "bottom" as const, horizontal: "left" as const },
  transformOrigin: { vertical: "top" as const, horizontal: "left" as const },
  PaperProps: { sx: {
    mt: 0.75, bgcolor: "#ffffff", color: "#202b3b", backgroundImage: "none",
    border: "1px solid #c4cedb", borderRadius: "4px", boxShadow: "0 8px 24px rgba(32,43,59,0.14)",
    maxHeight: 320, maxWidth: "calc(100% - 32px)", boxSizing: "border-box",
    "& .MuiMenuItem-root": {
      m: 0, px: 1.25, py: 1.25, minHeight: 44, borderRadius: "3px", fontSize: "0.8125rem", lineHeight: 1.5,
      whiteSpace: "normal", overflowWrap: "anywhere", color: "#202b3b",
      "&:hover": { bgcolor: "#f0f4f9" },
      "&.Mui-focusVisible": { bgcolor: "#e8eff9", outline: "2px solid #4268a8", outlineOffset: "-2px" },
      "&.Mui-selected": { bgcolor: "#e8eff9", color: "#284777", fontWeight: 600 },
      "&.Mui-selected:hover, &.Mui-selected.Mui-focusVisible": { bgcolor: "#dce7f6" },
    },
  } },
  MenuListProps: { sx: { p: 0.5 } },
};

export default function Dashboard({ account, busy, onGenerate, onCopy, onLogout }: Props) {
  const [productId, setProductId] = useState("");
  const destinationField = useRef<HTMLDivElement>(null);
  const [menuPlacement, setMenuPlacement] = useState({ above: false, height: 320 });
  const positionDestinationMenu = () => {
    const bounds = destinationField.current?.getBoundingClientRect();
    if (!bounds) return;
    // Leave room for the popup gap and viewport margin; Popover otherwise
    // shifts a tall menu over its field when it runs out of space below.
    const below = window.innerHeight - bounds.bottom - 24;
    const above = bounds.top - 24;
    const openAbove = below < 176 && above > below;
    setMenuPlacement({ above: openAbove, height: Math.max(44, Math.min(320, openAbove ? above : below)) });
  };
  const link = account.referral_url ? productReferralLink(account.referral_url, productId) : "";
  return <Stack spacing={2.5} sx={{ color: "#202b3b", "& button": { textTransform: "none" } }}>
    <Box component="header" sx={{ borderBottom: "1px solid #d9dfe6", pb: 3, pt: 1 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
        <Box sx={{ fontSize: "0.8125rem", fontWeight: 600, color: "#526075" }}>WaveMind</Box>
        <Box sx={{ fontSize: "0.75rem", color: "#526075", textTransform: "capitalize" }}>{account.approval_state}</Box>
      </Stack>
      <Box sx={{ mt: 2, maxWidth: 640 }}>
        <Box component="h1" sx={{ m: 0, fontSize: { xs: "1.65rem", sm: "2rem" }, fontWeight: 600, letterSpacing: "-0.025em", lineHeight: 1.1 }}>Affiliate dashboard</Box>
        <Box component="p" sx={{ mt: 1.5, mb: 0, fontSize: "0.875rem", lineHeight: 1.6, color: "#526075", overflowWrap: "anywhere" }}>
          Welcome back, {account.display_name}.
        </Box>
      </Box>
    </Box>

    <Box component="section" sx={{ ...panel, bgcolor: "#f3f5f8" }}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={3} justifyContent="space-between">
        <Box><Box sx={eyebrow}>Your rank</Box><Box component="h2" sx={{ ...heading, mt: 1, textTransform: "capitalize", display: "flex", alignItems: "center", gap: 1 }}>{account.tier?.code || "Awaiting tier setup"}</Box>
          {account.assigned_tier && <Box sx={{ fontSize: "0.75rem", mt: 1 }}>Starting rank: {account.assigned_tier.code} · Earned rank: {account.earned_tier?.code}</Box>}
        </Box>
        <Box><Box sx={eyebrow}>Paid sales</Box><Box sx={{ fontSize: "2.5rem", fontWeight: 600, lineHeight: 1.3 }}>{account.sales_count ?? 0}</Box></Box>
      </Stack>
      {account.next_tier ? <Box sx={{ mt: 3 }}><Box sx={{ fontSize: "0.8125rem", mb: 1 }}>{account.sales_to_next_tier} more sales to unlock <Box component="span" sx={{ textTransform: "capitalize", fontWeight: 700 }}>{account.next_tier.code}</Box> · {account.next_tier.commission_percentage}%</Box>
        <LinearProgress aria-label="Progress to next rank" variant="determinate" value={Math.min(100, Math.max(0, 100 * ((account.sales_count ?? 0) - (account.tier?.minimum_sales ?? 0)) / Math.max(1, (account.next_tier.minimum_sales ?? 0) - (account.tier?.minimum_sales ?? 0))))} sx={{ height: 4, borderRadius: 0, bgcolor: "#dce2ea", "& .MuiLinearProgress-bar": { bgcolor: "#4268a8" } }} />
      </Box> : account.tiers_enabled && <Box sx={{ fontSize: "0.8125rem", mt: 2 }}>Highest rank reached.</Box>}
      <Box sx={{ mt: 2, fontSize: "0.625rem", lineHeight: 1.4, color: "#526075" }}>Each paid checkout counts once. Fully refunded checkouts are excluded.{account.assigned_tier ? " Your starting rank is the minimum. Sales unlock higher ranks automatically." : ""}</Box>
    </Box>
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(0, 1.65fr) minmax(0, 1fr)" }, gap: 2.5 }}>
      <Box component="section" sx={{ ...panel, bgcolor: "#ffffff", display: "flex", flexDirection: "column" }}>
        <Box component="h2" sx={heading}>Your personal link</Box>
        <Box component="p" sx={{ mt: 1.25, mb: 3, fontSize: "0.8125rem", lineHeight: 1.7, color: "#526075", maxWidth: 440 }}>
          Share this link on your website, social profiles, or in video descriptions.
        </Box>
        {account.referral_url ? <Stack spacing={2.5} sx={{ mt: "auto" }}>
          <TextField ref={destinationField} id="affiliate-link-destination" select label="Link destination" value={productId} onChange={event => setProductId(event.target.value)}
            fullWidth size="small" InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true, onOpen: positionDestinationMenu, MenuProps: {
              ...destinationMenu,
              anchorOrigin: { vertical: menuPlacement.above ? "top" : "bottom", horizontal: "left" },
              transformOrigin: { vertical: menuPlacement.above ? "bottom" : "top", horizontal: "left" },
              PaperProps: { sx: { ...destinationMenu.PaperProps.sx, mt: menuPlacement.above ? -0.75 : 0.75, maxHeight: menuPlacement.height } },
            } }}
            sx={{ ...linkField, "&& .MuiSelect-select": { paddingRight: "40px", minHeight: "1.5em" }, "& .MuiSelect-icon": { color: "#526075", right: 12 } }}>
            <MenuItem value="">WaveMind homepage</MenuItem>
            {referralProducts.map(product => <MenuItem key={product.id} value={product.id}>{product.name}</MenuItem>)}
          </TextField>
          <TextField label="Your personal link" value={link} fullWidth size="small" InputProps={{ readOnly: true }}
            sx={{ ...linkField, "& .MuiInputBase-input": { ...linkField["& .MuiInputBase-input"], fontFamily: "monospace" } }} />
          <Button variant="contained" disableElevation startIcon={<ContentCopyRoundedIcon />} disabled={busy} onClick={() => onCopy(link)}
            sx={{ bgcolor: "#345a96", color: "#ffffff", borderRadius: "4px", py: 1.25, fontSize: "0.8125rem", "&:hover": { bgcolor: "#284777" } }}>Copy link</Button>
        </Stack> : <Button variant="contained" disableElevation endIcon={<ArrowForwardRoundedIcon />} disabled={busy} onClick={onGenerate}
          sx={{ alignSelf: "flex-start", bgcolor: "#345a96", color: "#ffffff", borderRadius: "4px", py: 1.25, px: 2.5, fontSize: "0.8125rem", "&:hover": { bgcolor: "#284777" } }}>Generate personal link</Button>}
        <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 0.75, color: "#526075", fontSize: "0.625rem", lineHeight: 1.4 }}>
          Choose a product or the homepage. Every link uses your permanent referral code.
        </Box>
      </Box>

      <Box component="section" sx={{ ...panel, bgcolor: "#ffffff", borderColor: "#d9dfe6", display: "flex", flexDirection: "column" }}>
        <Box sx={{ ...eyebrow, color: "#526075" }}>Your commission rate</Box>
        <Box sx={{ my: 2, fontSize: { xs: "2.5rem", sm: "3rem" }, fontWeight: 500, letterSpacing: "-0.03em", lineHeight: 1.1, fontVariantNumeric: "tabular-nums" }}>
          {Number(account.commission_percentage).toFixed(2)}%
        </Box>
        <Box component="p" sx={{ m: 0, mb: 3, fontSize: "0.8125rem", lineHeight: 1.6, color: "#526075" }}>Your assigned rate, managed by the WaveMind team.</Box>
      </Box>
    </Box>

    <Box component="section" aria-labelledby="affiliate-discount-heading" sx={{ ...panel, bgcolor: "#ffffff" }}>
      <Box component="h2" id="affiliate-discount-heading" sx={heading}>Your discount codes</Box>
      {account.discount_codes?.length ? <>
        <Box component="p" sx={{ mt: 1.25, mb: 2.5, fontSize: "0.8125rem", lineHeight: 1.7, color: "#526075" }}>
          Share these codes with your audience. Customers receive the discount at checkout, and the sale is credited to you even when they use another affiliate’s link.
        </Box>
        <Stack spacing={2}>
          {account.discount_codes.map(discount => <Stack key={discount.code} direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box component="code" sx={{ fontSize: "1rem", fontWeight: 600, overflowWrap: "anywhere", userSelect: "all" }}>{discount.code}</Box>
              <Box sx={{ mt: 0.5, fontSize: "0.8125rem", color: "#526075" }}>{Number(discount.percentage)}% off for your customers</Box>
            </Box>
            <Button startIcon={<ContentCopyRoundedIcon />} disabled={busy} aria-label={`Copy discount code ${discount.code}`}
              onClick={() => onCopy(discount.code, "code")} sx={{ color: "#345a96", fontSize: "0.8125rem", alignSelf: { xs: "flex-start", sm: "center" } }}>Copy code</Button>
          </Stack>)}
        </Stack>
      </> : <Box component="p" sx={{ mt: 1.25, mb: 0, fontSize: "0.8125rem", lineHeight: 1.7, color: "#526075" }}>No discount code has been assigned yet. Contact WaveMind support to request one.</Box>}
    </Box>

    <Box component="section" sx={{ ...panel, bgcolor: "#ffffff", display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "minmax(0, 1fr) minmax(0, 1fr)" }, gap: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0 }}>
        <Box sx={{ minWidth: 0, overflowWrap: "anywhere" }}>
          <Box component="h2" sx={{ ...eyebrow, mt: 0, mb: 0.75, color: "#526075" }}>Account details</Box>
          <Box sx={{ fontWeight: 600, fontSize: "0.875rem" }}>{account.display_name}</Box>
          <Box sx={{ mt: 0.5, color: "#526075", fontSize: "0.75rem" }}>{account.email}</Box>
          <Box sx={{ mt: 0.5, color: "#526075", fontSize: "0.625rem" }}>{account.email_verified_at ? "Email confirmed" : "Email not confirmed"}</Box>
        </Box>
      </Box>
      <Box sx={{ borderLeft: { sm: "1px solid #d9dfe6" }, pl: { sm: 3 }, alignSelf: "center" }}>
        <Box sx={{ ...eyebrow, color: "#526075", mb: 1 }}>30-day referral window</Box>
        <Box component="p" sx={{ m: 0, fontSize: "0.75rem", lineHeight: 1.7, color: "#526075" }}>Visitors who follow it are remembered for 30 days, unless they follow another referral link.</Box>
      </Box>
    </Box>
    <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={2}>
      <Box sx={{ color: "#526075", fontSize: "0.6875rem" }}>WaveMind support</Box>
      <Stack direction="row" spacing={1}>
        <Button href="/support" sx={{ color: "#345a96", fontSize: "0.75rem" }}>Contact support</Button>
        <Button disabled={busy} onClick={onLogout} sx={{ color: "#526075", fontSize: "0.75rem" }}>Log out</Button>
      </Stack>
    </Stack>
  </Stack>;
}
